import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiExternalLink, FiEye } from 'react-icons/fi';

/**
 * A carrier hand-off card inside a chat message.
 *
 * When a carrier automation reaches something only a licensed agent may
 * decide (an acknowledgement, or the screen where a policy change is made
 * or cancelled), the agent's reply carries a signed hand-off link
 * (automation/mcp-server/server.mjs, "/handoff/<runId>"). This turns that
 * link into a card in the chat's own style: the carrier's wording, the key
 * facts, and one bubble per choice. The card is the same for every carrier
 * — the automation describes the screen (lib/agentHandoff.ts → HandoffCard)
 * and carries out the choice in the portal; nothing here knows what a
 * particular portal looks like.
 *
 * A choice that changes a policy asks "are you sure?" first. The carrier's
 * actual screen is one click away for anyone who wants to see it.
 * Requests are fetched (not <img src>) so the tunnel's browser-warning
 * page can be skipped with a header.
 */

export const HANDOFF_LINK = /^https?:\/\/[^/\s]+\/handoff\/[A-Za-z0-9_-]+\?e=\d+&s=[a-f0-9]+$/;

const HEADERS = { 'ngrok-skip-browser-warning': '1' };
const ACCENT = '139,92,246';

type Choice = { id: string; label: string; kind: 'continue' | 'cancel' | 'binding'; confirm?: string; noInput?: boolean };
type CardInput = { id: string; label: string; kind: 'code' | 'text'; hint?: string };
type Card = { carrier: string; title: string; carrierText: string[]; facts?: { label: string; value: string }[]; inputs?: CardInput[]; notice?: string; choices: Choice[] };
type Phase = 'loading' | 'waiting' | 'working' | 'done' | 'expired' | 'unreachable';

/**
 * A choice bubble. Every bubble looks the same at rest — no choice is
 * pre-highlighted — and turns purple only under the pointer (or keyboard
 * focus), so the person can see which one they are about to click.
 */
const Bubble: React.FC<{ onClick: () => void; disabled?: boolean; children: React.ReactNode }> = ({ onClick, disabled, children }) => {
  const [hot, setHot] = useState(false);
  const lit = hot && !disabled;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHot(true)}
      onMouseLeave={() => setHot(false)}
      onFocus={() => setHot(true)}
      onBlur={() => setHot(false)}
      style={{
        padding: '9px 16px', borderRadius: 999, font: 'inherit', fontSize: 14, fontWeight: 600,
        cursor: disabled ? 'progress' : 'pointer', opacity: disabled ? 0.6 : 1, outline: 'none',
        border: `1.5px solid ${lit ? `rgb(${ACCENT})` : 'rgba(120,120,135,0.45)'}`,
        // White at rest so the choices stand out from the card; dark text keeps them readable in either theme.
        background: lit ? `rgb(${ACCENT})` : '#fff',
        color: lit ? '#fff' : '#1d1b29',
        boxShadow: lit ? `0 2px 8px rgba(${ACCENT},0.35)` : '0 1px 3px rgba(20,20,40,0.10)',
        transition: 'background 120ms, border-color 120ms, color 120ms, box-shadow 120ms',
      }}
    >
      {children}
    </button>
  );
};

/**
 * onFinished: called once when the person answered this card and the carrier then finished (e.g. a one-time code was accepted), so the
 * chat can tell the agent to carry on by itself — "it needs to automatically continue after I submit the login code" (Kieran,
 * 2026-10-26). Not called for a card that was already finished when it loaded.
 */
const answeredLinks = new Set<string>();
const reportedLinks = new Set<string>();
const chosenByLink = new Map<string, string[]>();

const CarrierHandoffFrame: React.FC<{ url: string; onFinished?: (summary: string) => void }> = ({ url, onFinished }) => {
  const [base, query] = url.split('?');
  const q = `?${query}`;
  const [phase, setPhase] = useState<Phase>('loading');
  const [card, setCard] = useState<Card | null>(null);
  const [message, setMessage] = useState('');
  const [confirming, setConfirming] = useState<Choice | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // What the person types on the card (e.g. a one-time code). Sent straight to the agency computer, never into the chat.
  const [values, setValues] = useState<Record<string, string>>({});
  const [showPicture, setShowPicture] = useState(false);
  // The "Open the full browser" button signs in first, which takes a few seconds: say what it is doing and how it went.
  const [openState, setOpenState] = useState<{ busy: boolean; text: string }>({ busy: false, text: '' });
  const [picture, setPicture] = useState<string | null>(null);
  const pictureRef = useRef<string | null>(null);
  const showPictureRef = useRef(false);
  showPictureRef.current = showPicture;
  const phaseRef = useRef<Phase>('loading');
  phaseRef.current = phase;
  // Kept per card link outside the component: the chat can redraw a message (and so this card) while the carrier finishes, which used to
  // lose the fact that the person had answered — and the agent was never told to carry on (2026-10-26, Liberty after its code).
  const answeredHere = { get current() { return answeredLinks.has(base); }, set current(v: boolean) { if (v) answeredLinks.add(base); } };
  const reported = { get current() { return reportedLinks.has(base); }, set current(v: boolean) { if (v) reportedLinks.add(base); } };
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;
  const chosenRef = useRef<string[]>([]);
  chosenRef.current = chosen.length ? chosen : (chosenByLink.get(base) ?? []);
  useEffect(() => {
    if (phase !== 'done' || !answeredHere.current || reported.current || !onFinishedRef.current) return;
    reported.current = true;
    onFinishedRef.current(chosenRef.current.join('; '));
    // Also re-checked when sending becomes possible: the chat withholds it while the agent is still replying, and a card that finished
    // in that moment used to drop the "continue" message for good.
  }, [phase, !!onFinished]);

  useEffect(() => {
    let live = true;
    let timer: number | undefined;
    const tick = async () => {
      if (!live) return;
      try {
        const res = await fetch(`${base}/state${q}`, { headers: HEADERS });
        if (res.status === 403) { setPhase('expired'); return; }
        const s = await res.json();
        if (!s.waiting) {
          setCard(null);
          // Between two hand-offs the run is working again; keep watching for the next screen.
          if (s.status === 'running') { setPhase('working'); timer = window.setTimeout(tick, 1200); return; }
          setPhase(s.status === 'unknown' ? 'expired' : 'done');
          return;
        }
        setCard((prev) => (prev && s.card && prev.title === s.card.title && prev.notice === s.card.notice ? prev : s.card));
        setMessage(s.message || '');
        // The person just chose something and the run is asking again (often the very same question, e.g. "no result on that page yet"):
        // the buttons must come back, not stay locked waiting for a question that looks different.
        if (phaseRef.current === 'working' && !s.chosen) setBusy(false);
        setPhase('waiting');
        if (showPictureRef.current && s.picture) {
          const frame = await fetch(`${base}/frame${q}`, { headers: HEADERS, cache: 'no-store' });
          if (frame.ok && live) {
            const next = URL.createObjectURL(await frame.blob());
            if (pictureRef.current) URL.revokeObjectURL(pictureRef.current);
            pictureRef.current = next;
            setPicture(next);
          }
        }
      } catch {
        if (live) setPhase((prev) => (prev === 'loading' ? 'unreachable' : prev));
      }
      timer = window.setTimeout(tick, 1200);
    };
    tick();
    return () => {
      live = false;
      if (timer) window.clearTimeout(timer);
      if (pictureRef.current) URL.revokeObjectURL(pictureRef.current);
    };
  }, [base, q]);

  // A new screen means a fresh decision.
  useEffect(() => { setConfirming(null); setError(''); setBusy(false); setValues({}); }, [card?.title, card?.notice]);

  const post = useCallback(
    (path: string, body: unknown) =>
      fetch(`${base}/${path}${q}`, { method: 'POST', headers: { ...HEADERS, 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then((r) => r.json()),
    [base, q],
  );

  const choose = async (choice: Choice, confirmed = false) => {
    if (busy) return;
    if (choice.kind === 'binding' && !confirmed) { setConfirming(choice); return; }
    setBusy(true);
    setError('');
    try {
      const result = await post('choose', { choice: choice.id, confirmed, values });
      setValues({});
      if (result?.ok) {
        answeredHere.current = true;
        chosenByLink.set(base, [...(chosenByLink.get(base) ?? []), `${card?.title ?? ''}: ${choice.label}`]);
        setChosen((prev) => [...prev, `${card?.title ?? ''}: ${choice.label}`]);
        setConfirming(null);
        setPhase('working');
      } else {
        setError(result?.message || 'That could not be sent to the carrier portal.');
        setBusy(false);
      }
    } catch {
      setError('The agency computer could not be reached.');
      setBusy(false);
    }
  };

  const shell: React.CSSProperties = { border: `1.5px solid rgba(${ACCENT},0.55)`, borderRadius: 16, margin: '12px 0', background: `rgba(${ACCENT},0.05)`, maxWidth: 620, color: 'inherit', overflow: 'hidden' };
  const pad: React.CSSProperties = { padding: '14px 16px' };
  const small: React.CSSProperties = { fontSize: 12, opacity: 0.75 };
  const line = (icon: React.ReactNode, body: React.ReactNode) => (
    <div style={shell}><div style={{ ...pad, display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, lineHeight: 1.45 }}>{icon}<span>{body}</span></div></div>
  );

  if (phase === 'done') return line(<FiCheckCircle size={16} style={{ marginTop: 2, flexShrink: 0 }} />, <>Finished in the carrier portal.{(chosen.length ? chosen : chosenByLink.get(base) ?? []).length ? ` You chose — ${(chosen.length ? chosen : chosenByLink.get(base) ?? []).join('; ')}.` : ''}</>);
  if (phase === 'expired') return line(<FiAlertTriangle size={16} style={{ marginTop: 2, flexShrink: 0 }} />, 'This hand-off has expired. Ask the agent to check the status of the change to get a fresh one.');
  if (phase === 'unreachable') return line(<FiAlertTriangle size={16} style={{ marginTop: 2, flexShrink: 0 }} />, 'The agency computer running the carrier browser could not be reached.');
  if (phase === 'loading') return line(<FiEye size={16} style={{ marginTop: 2, flexShrink: 0 }} />, 'Checking the carrier portal…');
  if (phase === 'working') return line(<FiEye size={16} style={{ marginTop: 2, flexShrink: 0 }} />, <>Working in the carrier portal…{chosen.length ? ` You chose — ${chosen[chosen.length - 1]}.` : ''} If another decision is needed it will appear here.</>);

  // Waiting, but this screen has no card: it is not mapped, so the real browser was brought up instead.
  if (!card) return line(<FiExternalLink size={16} style={{ marginTop: 2, flexShrink: 0 }} />, <><strong>The carrier browser has been opened on the agency computer.</strong> {message}</>);

  return (
    <div style={shell}>
      <div style={{ ...pad, borderBottom: `1px solid rgba(${ACCENT},0.22)` }}>
        {/* Who is asking comes first and largest: the same card serves every carrier. */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
          <span style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.15, letterSpacing: -0.2, color: `rgb(${ACCENT})` }}>{card.carrier}</span>
          <span style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, opacity: 0.75 }}>needs your decision</span>
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}>{card.title}</div>
      </div>

      {card.facts && card.facts.length > 0 && (
        <div style={{ ...pad, display: 'grid', gridTemplateColumns: 'minmax(120px, auto) 1fr', gap: '6px 14px', fontSize: 14, borderBottom: `1px solid rgba(${ACCENT},0.22)` }}>
          {card.facts.map((f) => (
            <React.Fragment key={f.label}>
              <span style={{ opacity: 0.7 }}>{f.label}</span>
              <strong>{f.value}</strong>
            </React.Fragment>
          ))}
        </div>
      )}

      {card.carrierText.length > 0 && (
        <div style={pad}>
          <div style={{ ...small, marginBottom: 6 }}>{card.carrier}&apos;s wording, exactly as shown in the portal:</div>
          <div style={{ borderLeft: `3px solid rgba(${ACCENT},0.6)`, paddingLeft: 12, fontSize: 14, lineHeight: 1.5 }}>
            {card.carrierText.map((t, i) => <p key={i} style={{ margin: i ? '8px 0 0' : 0 }}>{t}</p>)}
          </div>
        </div>
      )}

      {card.inputs && card.inputs.length > 0 && (
        <div style={{ ...pad, paddingTop: 0 }}>
          {card.notice && <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, display: 'flex', gap: 6 }}><FiAlertTriangle size={14} style={{ marginTop: 2 }} /> {card.notice}</div>}
          {card.inputs.map((input) => (
            <label key={input.id} style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>
              {input.label}
              <input
                type="text"
                inputMode={input.kind === 'code' ? 'numeric' : 'text'}
                autoComplete={input.kind === 'code' ? 'one-time-code' : 'off'}
                value={values[input.id] ?? ''}
                disabled={busy}
                onChange={(e) => setValues((prev) => ({ ...prev, [input.id]: input.kind === 'code' ? e.target.value.replace(/\D/g, '').slice(0, 10) : e.target.value }))}
                style={{ display: 'block', marginTop: 6, width: input.kind === 'code' ? 180 : '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid rgba(120,120,135,0.45)', background: '#fff', color: '#1d1b29', font: 'inherit', fontSize: input.kind === 'code' ? 20 : 14, letterSpacing: input.kind === 'code' ? 4 : 0, fontWeight: 600 }}
              />
              {input.hint && <span style={{ display: 'block', marginTop: 4, fontWeight: 400, opacity: 0.75, fontSize: 12 }}>{input.hint}. It goes straight to the carrier, not into this chat.</span>}
            </label>
          ))}
        </div>
      )}

      <div style={{ ...pad, paddingTop: 4 }}>
        {confirming ? (
          <div style={{ border: `1.5px solid rgb(${ACCENT})`, borderRadius: 12, padding: 12 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
              <FiAlertTriangle size={16} style={{ marginTop: 2, flexShrink: 0 }} />
              <span>{confirming.confirm || 'This will change the policy. Are you sure?'}</span>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Bubble disabled={busy} onClick={() => choose(confirming, true)}>Yes — {confirming.label.toLowerCase()}</Bubble>
              <Bubble disabled={busy} onClick={() => setConfirming(null)}>No, go back</Bubble>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {card.choices.map((c) => (
              <Bubble key={c.id} disabled={busy || (c.kind !== 'cancel' && !c.noInput && (card.inputs ?? []).some((i) => !(values[i.id] ?? '').trim()))} onClick={() => choose(c)}>{c.label}</Bubble>
            ))}
          </div>
        )}
        {error && <div style={{ marginTop: 10, fontSize: 13, display: 'flex', gap: 6 }}><FiAlertTriangle size={14} style={{ marginTop: 2 }} /> {error}</div>}
      </div>

      <div style={{ ...pad, ...small, borderTop: `1px solid rgba(${ACCENT},0.22)`, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <span>Nothing happens in the portal until you choose.</span>
        <span style={{ display: 'flex', gap: 14 }}>
          <button type="button" onClick={() => setShowPicture((v) => !v)} style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}>
            <FiEye size={11} /> {showPicture ? 'Hide' : 'Show'} the carrier&apos;s screen
          </button>
          <button
            type="button"
            disabled={openState.busy}
            onClick={async () => {
              setOpenState({ busy: true, text: 'Signing in and opening the portal on the agency computer…' });
              try {
                const r = await post('show', {});
                setOpenState({ busy: false, text: r.ok ? (r.signedIn ? 'Opened, signed in, on the agency computer.' : 'Opened on the agency computer.') : (r.message || 'Could not open it.') });
              } catch {
                setOpenState({ busy: false, text: 'Could not reach the agency computer to open it.' });
              }
            }}
            style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: openState.busy ? 'progress' : 'pointer', padding: 0, font: 'inherit', opacity: openState.busy ? 0.6 : 1 }}
          >
            <FiExternalLink size={11} /> Open the full browser
          </button>
        </span>
      </div>
      {openState.text && <div style={{ ...pad, ...small, paddingTop: 0 }}>{openState.text}</div>}
      {showPicture && (picture
        ? <img src={picture} alt="The carrier portal screen that is waiting" style={{ display: 'block', width: '100%', background: '#fff' }} />
        : <div style={{ ...pad, ...small }}>Loading the carrier&apos;s screen…</div>)}
    </div>
  );
};

export default CarrierHandoffFrame;
